/**
 * React hook for managing Winwheel.js canvas library
 */

import { useRef, useEffect, useCallback, useMemo } from 'react';
// @ts-expect-error - Winwheel is a JS library without types
import { Winwheel } from '../lib/Winwheel';
import { generateSegments, getThemeColors } from '../utils/colors';
import type { WheelEntry, WheelSegment, ColorTheme } from '../types/wheel';
import type { WinwheelInstance, WinwheelSegment } from '../types/winwheel.d';

const MAX_SEGMENTS = 60;
const TEXT_SIZE_BASE = 26;

interface UseWinwheelOptions {
  canvasId: string;
  entries: WheelEntry[];
  canvasSize?: number;
  pointerAngle?: number;
  colors?: string[];
  colorTheme?: ColorTheme;
  spinDuration?: number;
  spinRevolutions?: number;
  onSegmentChange?: (segment: WheelSegment) => void;
  onSpinComplete?: (segment: WheelSegment) => void;
  onTickSound?: () => void;
}

interface UseWinwheelReturn {
  wheelRef: React.MutableRefObject<WinwheelInstance | null>;
  startIdleSpin: () => void;
  startRealSpin: (winnerIndex: number) => void;
  stopSpin: () => void;
  redraw: () => void;
}

/**
 * Calculate font size based on segment count
 */
function calculateFontSize(count: number): number {
  return TEXT_SIZE_BASE - Math.floor(count / 4);
}

/**
 * Calculate max text length based on entry count
 */
function calculateMaxTextLength(count: number): number {
  return 121 - Math.floor(count / 4) * 1.125;
}

/**
 * Cheap signature of just what affects the wheel face. Only the first
 * MAX_SEGMENTS entries are rendered, so we only hash those — keeps the
 * effect cost O(MAX_SEGMENTS) instead of O(entries.length).
 */
function wheelFaceSignature(entries: WheelEntry[]): string {
  const visible = Math.min(entries.length, MAX_SEGMENTS);
  let sig = entries.length + '|';
  for (let i = 0; i < visible; i++) {
    const e = entries[i];
    sig += (e.id || i) + ':' + e.name + '\n';
  }
  return sig;
}

export function useWinwheel(options: UseWinwheelOptions): UseWinwheelReturn {
  const wheelRef = useRef<WinwheelInstance | null>(null);
  const prevSignatureRef = useRef<string>('');
  const optionsRef = useRef(options);

  // Keep options ref updated
  optionsRef.current = options;

  // Generate wheel segments from entries
  // generateSegments handles the >60 entries case automatically
  const segments = useMemo(() => {
    const segmentCount = Math.min(options.entries.length, MAX_SEGMENTS);
    const maxTextWidth = calculateMaxTextLength(segmentCount);
    const fontSize = calculateFontSize(segmentCount);

    // Use custom colors if provided, otherwise get colors from theme
    let colors = options.colors;
    if (!colors && options.colorTheme && options.colorTheme !== 'default') {
      colors = getThemeColors(options.colorTheme, options.entries.length);
    }

    return generateSegments(options.entries, maxTextWidth, fontSize, colors);
  }, [options.entries, options.colors, options.colorTheme]);

  // Start idle spin animation
  const startIdleSpin = useCallback(() => {
    const { canvasId, pointerAngle = 309 } = optionsRef.current;

    // Check if canvas exists in DOM
    const canvas = document.getElementById(canvasId);
    if (!canvas) {
      console.warn('Canvas not found:', canvasId);
      return;
    }

    // Preserve rotation angle if wheel exists
    const rotationAngle = wheelRef.current?.rotationAngle || 0;

    // Clear existing wheel
    if (wheelRef.current) {
      wheelRef.current.stopAnimation(false);
      wheelRef.current.clearCanvas();
    }

    wheelRef.current = new Winwheel({
      canvasId,
      pointerAngle,
      rotationAngle,
      numSegments: segments.length,
      segments,
      drawText: true,
      textFontFamily: 'Arial',
      textFontSize: calculateFontSize(segments.length),
      textFontWeight: 'bold',
      textOrientation: 'horizontal',
      textAlignment: 'outer',
      textMargin: 10,
      textFillStyle: 'white',
      strokeStyle: null, // Per-segment strokeStyle takes precedence
      lineWidth: 1,
      animation: {
        type: 'spinOngoing',
        duration: 1000,
        spins: 10,
        easing: 'Linear.easeNone',
        // Don't clear the on-screen canvas per tick — Winwheel.draw() now
        // keeps the face static on the canvas and rotates the DOM element
        // via CSS transform instead.
        clearTheCanvas: false,
      },
    }) as WinwheelInstance;

    wheelRef.current.startAnimation();
  }, [segments]);

  // Start real spin with winner selection
  const startRealSpin = useCallback(
    (winnerIndex: number) => {
      const {
        canvasId,
        pointerAngle = 309,
        spinDuration = 10,
        spinRevolutions = 7,
        onSegmentChange,
        onSpinComplete,
        onTickSound,
      } = optionsRef.current;

      // Clear existing wheel
      if (wheelRef.current) {
        wheelRef.current.stopAnimation(false);
        wheelRef.current.clearCanvas();
      }

      // Calculate stop angle based on ENTRIES (not segments)
      // This ensures correct winner selection even when >60 entries
      const entries = optionsRef.current.entries;
      const entryAngle = 360 / entries.length;
      const stopAngle = entryAngle * winnerIndex + entryAngle / 2;

      wheelRef.current = new Winwheel({
        canvasId,
        pointerAngle,
        rotationAngle: 0,
        numSegments: segments.length,
        segments,
        drawText: true,
        textFontFamily: 'Arial',
        textFontSize: calculateFontSize(segments.length),
        textFontWeight: 'bold',
        textOrientation: 'horizontal',
        textAlignment: 'outer',
        textMargin: 10,
        textFillStyle: 'white',
        strokeStyle: null, // Per-segment strokeStyle takes precedence
        lineWidth: 1,
        animation: {
          type: 'spinToStop',
          duration: spinDuration,
          spins: spinRevolutions,
          easing: 'Power1.easeOut',
          stopAngle,
          // Don't clear the on-screen canvas per tick — see startIdleSpin.
          clearTheCanvas: false,
          callbackFinished: (segment: WinwheelSegment & { fullText?: string }) => {
            // Get the winner's segment color from our segments array for reliability
            const winnerSegmentIndex = winnerIndex % segments.length;
            const fillStyle = segment.fillStyle || segments[winnerSegmentIndex]?.fillStyle || '';

            onSpinComplete?.({
              fillStyle,
              text: segment.text || '',
              fullText: segment.fullText || segment.text || '',
            });
          },
          callbackAfter: () => {
            const wheel = wheelRef.current;
            if (!wheel) return;

            const segment = wheel.getIndicatedSegment();
            const entries = optionsRef.current.entries;
            const pointerAngle = optionsRef.current.pointerAngle || 309;

            // For >60 entries, calculate entry from rotation angle (not segment)
            // This ensures the label shows correct entry names even when wheel has fewer segments
            const rotationAngle = wheel.rotationAngle % 360;
            const entryAngle = 360 / entries.length;
            let indicatedAngle = pointerAngle - rotationAngle;
            if (indicatedAngle < 0) indicatedAngle += 360;
            if (indicatedAngle >= 360) indicatedAngle -= 360;
            const entryIndex = Math.floor(indicatedAngle / entryAngle) % entries.length;
            const entry = entries[entryIndex];

            // Get fillStyle from segment, or from our segments array as fallback
            const segmentIndex = Math.floor(indicatedAngle / (360 / segments.length)) % segments.length;
            const fillStyle = segment?.fillStyle || segments[segmentIndex]?.fillStyle || '';

            onSegmentChange?.({
              fillStyle,
              text: entry?.name || segment?.text || '',
              fullText: entry?.name || segment?.fullText || '',
            });
          },
          callbackSound: onTickSound,
        },
      }) as WinwheelInstance;

      wheelRef.current.startAnimation();
    },
    [segments]
  );

  // Stop current animation
  const stopSpin = useCallback(() => {
    if (wheelRef.current) {
      wheelRef.current.stopAnimation(false);
    }
  }, []);

  // Redraw wheel
  const redraw = useCallback(() => {
    if (wheelRef.current) {
      wheelRef.current.draw(true);
    }
  }, []);

  // Initialize/reinitialize wheel when entries change
  useEffect(() => {
    const signature = wheelFaceSignature(options.entries);
    if (signature !== prevSignatureRef.current) {
      prevSignatureRef.current = signature;
      // Use requestAnimationFrame to ensure canvas is in DOM
      const rafId = requestAnimationFrame(() => {
        startIdleSpin();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [options.entries, startIdleSpin]);

  // Reinitialize wheel when colors or colorTheme change
  useEffect(() => {
    // Only reinitialize if wheel already exists (not on initial mount)
    if (wheelRef.current) {
      const rafId = requestAnimationFrame(() => {
        startIdleSpin();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [options.colors, options.colorTheme, startIdleSpin]);

  // Also run on initial mount with a fallback
  useEffect(() => {
    // Double-check initialization after a short delay
    const timer = setTimeout(() => {
      if (!wheelRef.current) {
        startIdleSpin();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [startIdleSpin]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wheelRef.current) {
        wheelRef.current.stopAnimation(false);
        wheelRef.current.clearCanvas();
      }
    };
  }, []);

  return {
    wheelRef,
    startIdleSpin,
    startRealSpin,
    stopSpin,
    redraw,
  };
}
